// AdminModal.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import RichTextEditor from './RichTextEditor';
import { supabase } from '../supabaseClient';

/** ===========================
 *  Upload helper (frontend -> API)
 *  =========================== */
const API_BASE = process.env.REACT_APP_API_BASE || '';
// Optional client-side API key (note: exposing this to the browser is less secure)
const CLIENT_UPLOAD_API_KEY = process.env.REACT_APP_UPLOAD_API_KEY || '';

// Direct upload to Supabase Storage (bypasses Vercel 4.5MB limit)
async function uploadDirectToSupabase(file, projectId) {
  const bucket = 'images';
  const safeFilename = (file.name || 'image').replace(/[^a-zA-Z0-9_.-]/g, '_');
  const remotePath = `${projectId}/${Date.now()}-${safeFilename}`;
  
  const { error } = await supabase.storage
    .from(bucket)
    .upload(remotePath, file, {
      contentType: file.type,
      upsert: true
    });
  
  if (error) {
    console.error('Direct Supabase upload error:', error);
    throw new Error(error.message || 'Upload failed');
  }
  
  const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(remotePath);
  return publicUrlData?.publicUrl;
}

async function uploadImageToServer(file, projectId) {
  // For large files (>4MB), upload directly to Supabase to bypass Vercel limit
  if (file.size > 4 * 1024 * 1024) {
    console.log('Large file detected, using direct Supabase upload');
    return uploadDirectToSupabase(file, projectId);
  }
  
  const formData = new FormData();
  formData.append('image', file);
  formData.append('projectId', projectId); // 👈 important

  // Try to add Authorization: Bearer <access_token> if available
  let headers = {};
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    // ignore
  }
  // Add client-side API key header if configured
  if (CLIENT_UPLOAD_API_KEY) headers['x-api-key'] = CLIENT_UPLOAD_API_KEY;

  const res = await fetch(`${API_BASE}/api/upload-image`, {
    method: 'POST',
    body: formData,
    headers,
  });
  // Some responses may not be JSON (e.g. Vercel HTML error). Handle that gracefully.
  let data;
  const text = await res.text();
  try {
    data = JSON.parse(text);
  } catch (e) {
    data = { error: text };
  }
  if (!res.ok) {
    const errMsg = data?.error || `Upload failed: ${res.status}`;
    throw new Error(errMsg);
  }
  return data.url; // e.g. /images/<projectId>/<filename>
}

function AdminModal({ view, item, onSave, onClose, saving, allProjects }) {
  const isProjects = view === 'projects';
  const isCvProjects = view === 'cv-projects';
  const isHomepageImages = view === 'homepage-images';
  const isHomepageSetup = view === 'homepage-setup';
  const isPageTexts = view === 'page-texts';
  const isBanner = view === 'banner';

  const initialForm = useMemo(() => {
    if (isProjects) {
      return {
        project_title: '',
        description: '',
        pendingFiles: [],
        content: [[], []],
      };
    }
    if (isCvProjects) {
      return {
        title: '',
        category: '',
        project_id: null,
        external_url: '',
        linkMode: 'none', // 'none' | 'project' | 'external'
      };
    }
    if (isHomepageImages) {
      return {
        url: '',
        name: '',
        is_primary: false,
        link_type: 'none', // 'none' | 'internal' | 'external'
        link_project_id: null,
        link_url: '',
      };
    }
    if (isHomepageSetup) {
      return {
        title: '',
        subtitle: '',
        preview_url: '',
        project_id: null,
      };
    }
    if (isBanner) {
      return {
        enabled: false,
        image_url: '',
        alt_text: 'Banner',
        link_type: 'none',
        link_url: '',
        link_project_id: null,
        max_width: 100,
        max_height: 300,
      };
    }
    if (isPageTexts) {
      return {
        name: '',
        text: '',
      };
    }
    return {
      title: '',
      date: '',
      venue: '',
      external_url: '',
      description: '',
      pendingFiles: [],
      content: [[], []],
    };
  }, [isProjects, isCvProjects, isHomepageImages, isHomepageSetup, isPageTexts, isBanner]);

  const [form, setForm] = useState(initialForm);

  // Note: the component no longer keeps a separate `images` grid. Images are
  // represented as content blocks inside `form.content`. We still track block
  // upload states below.
  // Content editing: drag state and image upload states for content blocks
  const [contentDragged, setContentDragged] = useState(null); // { col, idx }
  const [contentImageStates, setContentImageStates] = useState([[], []]); // mirrors form.content
  const [descriptionUploading, setDescriptionUploading] = useState(false);

  const mountedRef = useRef(true);
  
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // hydrate from item: when editing an existing item, populate the form
  useEffect(() => {
    if (!item) {
      setForm(initialForm);
      setContentImageStates([[], []]);
      return;
    }
    if (isProjects) {
      // Content may be stored as a JSON string in the DB; parse it if needed.
      let content = item.content;
      if (typeof content === 'string') {
        try {
          content = JSON.parse(content);
        } catch (e) {
          console.warn('AdminModal: failed to parse content JSON for item', item?.id);
          content = [[], []];
        }
      }
      if (!Array.isArray(content)) content = [[], []];
      setForm({
        project_title: item.project_title ?? '',
        description: item.description ?? '',
        pendingFiles: [],
        content,
      });
      setContentImageStates((content || [[], []]).map(col => col.map(() => 'idle')));
    } else if (isCvProjects) {
      setForm({
        title: item.title ?? '',
        category: item.category ?? '',
        project_id: item.project_id ?? null,
        external_url: item.external_url ?? '',
        linkMode: item.external_url ? 'external' : (item.project_id ? 'project' : 'none'),
      });
      setContentImageStates([[], []]);
    } else if (isHomepageImages) {
      // Determine link type from existing data
      let linkType = 'none';
      if (item.link_project_id) linkType = 'internal';
      else if (item.link_url) linkType = 'external';
      
      setForm({
        url: item.url ?? '',
        name: item.name ?? '',
        is_primary: item.is_primary ?? false,
        link_type: linkType,
        link_project_id: item.link_project_id ?? null,
        link_url: item.link_url ?? '',
      });
      setContentImageStates([[], []]);
    } else if (isHomepageSetup) {
      setForm({
        title: item.title ?? '',
        subtitle: item.subtitle ?? '',
        preview_url: item.preview_url ?? '',
        project_id: item.project_id ?? null,
      });
      setContentImageStates([[], []]);
    } else if (isBanner) {
      let config = {};
      if (item?.text) {
        try {
          config = JSON.parse(item.text);
        } catch (e) {
          config = {};
        }
      }
      setForm({
        enabled: config.enabled ?? false,
        image_url: config.image_url ?? '',
        alt_text: config.alt_text ?? 'Banner',
        link_type: config.link_type ?? 'none',
        link_url: config.link_url ?? '',
        link_project_id: config.link_project_id ?? null,
        max_width: config.max_width ?? 100,
        max_height: config.max_height ?? 300,
      });
      setContentImageStates([[], []]);
    } else if (isPageTexts) {
      setForm({
        name: item.name ?? '',
        text: item.text ?? '',
      });
      setContentImageStates([[], []]);
    } else {
      setForm({
        title: item.title ?? '',
        date: item.date ?? '',
        venue: item.venue ?? '',
        external_url: item.external_url ?? '',
        description: item.description ?? '',
        pendingFiles: [],
        content: [[], []],
      });
      setContentImageStates([[], []]);
    }
  }, [item, isProjects, isCvProjects, isHomepageImages, isHomepageSetup, isPageTexts, isBanner, initialForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // keep numeric project ids as-is (strings from inputs will be converted on submit)
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleLinkModeChange = (mode) => {
    setForm(f => ({ ...f, linkMode: mode }));
  };

  /** Add multiple files (for new project)
   * Instead of storing an `images` array, we create content blocks of type
   * 'image' and keep pending files with generated ids so AdminCMS can upload
   * them and replace the placeholders with final URLs on save.
   */
  // eslint-disable-next-line no-unused-vars
  const handleAddMultipleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setForm((f) => {
      const nextPending = Array.isArray(f.pendingFiles) ? [...f.pendingFiles] : [];
      const nextContent = f.content ? f.content.map(c => [...c]) : [[], []];

      files.forEach((file) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        // store pending file as object so we can match it later
        nextPending.push({ id, file });
        const preview = URL.createObjectURL(file);
        // choose column by balancing current lengths
        const leftLen = nextContent[0].length;
        const rightLen = nextContent[1].length;
        const col = leftLen <= rightLen ? 0 : 1;
        nextContent[col].push({ type: 'image', value: preview, __pendingId: id });
      });

      return { ...f, pendingFiles: nextPending, content: nextContent };
    });

    // clear file input selection
    e.target.value = '';
  };

  // Content block helpers
  const handleAddBlock = (colIdx, type) => {
    setForm(f => {
      const content = f.content ? f.content.map(c => [...c]) : [[], []];
      const block = { type, value: type === 'text' ? '' : null };
      content[colIdx].push(block);
      return { ...f, content };
    });
    setContentImageStates(s => {
      const next = s.map(arr => [...arr]);
      // new block state only relevant for image types (others get idle placeholders)
      next[colIdx].push('idle');
      return next;
    });
    // If image, open file picker immediately once the new input exists
    if (type === 'image') {
      setTimeout(() => {
        const cidx = (document.querySelectorAll(`[id^=file-input-${colIdx}-]`) || []).length - 1;
        const el = document.getElementById(`file-input-${colIdx}-${cidx}`);
        if (el) el.click();
      }, 50);
    }
  };

  const handleEditBlock = (colIdx, idx, newValue, newDescription) => {
    setForm(f => {
      const content = f.content.map(c => [...c]);
      content[colIdx][idx] = { ...content[colIdx][idx], value: newValue };
      if (newDescription !== undefined) {
        content[colIdx][idx] = { ...content[colIdx][idx], description: newDescription };
      }
      return { ...f, content };
    });
  };

  const handleRemoveBlock = (colIdx, idx) => {
    setForm(f => {
      const content = f.content.map(c => [...c]);
      content[colIdx].splice(idx, 1);
      return { ...f, content };
    });
    setContentImageStates(s => {
      const next = s.map(arr => [...arr]);
      next[colIdx].splice(idx, 1);
      return next;
    });
  };

  const handleContentDragStart = (colIdx, idx) => setContentDragged({ col: colIdx, idx });

  const handleContentDrop = (colIdx, idx) => {
    if (!contentDragged) return;
    const { col: fromCol, idx: fromIdx } = contentDragged;
    // no-op
    if (fromCol === colIdx && fromIdx === idx) { setContentDragged(null); return; }
    setForm(f => {
      const content = f.content.map(c => [...c]);
      const [block] = content[fromCol].splice(fromIdx, 1);
      let insertIdx = idx;
      if (fromCol === colIdx && fromIdx < idx) insertIdx = idx - 1;
      // allow drop at end
      if (insertIdx > content[colIdx].length) content[colIdx].push(block);
      else content[colIdx].splice(insertIdx, 0, block);
      return { ...f, content };
    });
    setContentImageStates(s => {
      const next = s.map(arr => [...arr]);
      const [state] = next[fromCol].splice(fromIdx, 1);
      let insertIdx = idx;
      if (fromCol === colIdx && fromIdx < idx) insertIdx = idx - 1;
      if (insertIdx > next[colIdx].length) next[colIdx].push(state);
      else next[colIdx].splice(insertIdx, 0, state);
      return next;
    });
    setContentDragged(null);
  };

  const handleBlockImageUpload = async (e, colIdx, idx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setContentImageStates(s => {
      const next = s.map(arr => [...arr]);
      next[colIdx][idx] = 'uploading';
      return next;
    });
    try {
      const url = await uploadImageToServer(file, item?.id);
      if (!mountedRef.current) return;
      setForm(f => {
        const content = f.content.map(c => [...c]);
        content[colIdx][idx] = { ...content[colIdx][idx], value: url };
        return { ...f, content };
      });
      setContentImageStates(s => {
        const next = s.map(arr => [...arr]);
        next[colIdx][idx] = 'idle';
        return next;
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setContentImageStates(s => {
        const next = s.map(arr => [...arr]);
        next[colIdx][idx] = 'error';
        return next;
      });
      alert(err.message || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const retryBlockUpload = async (colIdx, idx) => {
    const input = document.getElementById(`file-input-${colIdx}-${idx}`);
    if (input) input.click();
  };

  // (removed add-multiple-file handler per request: multi-file upload UI has been removed)

  // Note: images grid and per-slot uploads have been removed. Multi-file
  // additions create content image blocks with `__pendingId` and pendingFiles
  // holds [{id,file}]. Individual content image uploads are handled by
  // `handleBlockImageUpload` above which uploads immediately and replaces value.

  // removed images grid drag/drop handlers (content blocks have their own drag/drop)

  const handleSubmit = () => {
    let payload = { ...form };
    // Normalize CV project link fields
    if (isCvProjects) {
      if (form.linkMode === 'project') {
        payload.project_id = form.project_id ? Number(form.project_id) : null;
        payload.external_url = null;
      } else if (form.linkMode === 'external') {
        payload.external_url = form.external_url || null;
        payload.project_id = null;
      } else {
        payload.project_id = null;
        payload.external_url = null;
      }
    }
    // Normalize homepage images link fields
    if (isHomepageImages) {
      if (form.link_type === 'internal') {
        payload.link_project_id = form.link_project_id ? Number(form.link_project_id) : null;
        payload.link_url = null;
      } else if (form.link_type === 'external') {
        payload.link_url = form.link_url || null;
        payload.link_project_id = null;
      } else {
        payload.link_project_id = null;
        payload.link_url = null;
      }
      // Remove the link_type field as it's only used for UI
      delete payload.link_type;
    }
    if (isBanner) {
      if (form.link_type === 'internal') {
        payload.link_project_id = form.link_project_id ? Number(form.link_project_id) : null;
        payload.link_url = null;
      } else if (form.link_type === 'external') {
        payload.link_url = form.link_url || null;
        payload.link_project_id = null;
      } else {
        payload.link_project_id = null;
        payload.link_url = null;
      }
      payload.max_width = payload.max_width ? Number(payload.max_width) : null;
      payload.max_height = payload.max_height ? Number(payload.max_height) : null;
    }
    if (isHomepageSetup) {
      payload.project_id = form.project_id ? Number(form.project_id) : null;
    }
    // For page-texts, don't include id since we use name as the key
    if (!isPageTexts && !isBanner && item?.id) payload.id = item.id;
    onSave(payload);
  };

  const fields = isProjects
    ? [
        ['project_title', 'Project title', 'text'],
      ]
    : isCvProjects
    ? [
        ['title', 'Title', 'text'],
        ['category', 'Category', 'select'],
        ['project_id', 'Link to Project', 'project-select'],
      ]
    : isHomepageImages || isHomepageSetup || isBanner
    ? []
    : isPageTexts
    ? [
        ['name', 'Name (e.g. cv-bio)', 'text'],
      ]
    : [
        ['title', 'Title', 'text'],
        ['date', 'Date (YYYY-MM-DD)', 'text'],
        ['venue', 'Venue', 'text'],
        ['external_url', 'External URL', 'text'],
      ];

  const anyUploading = descriptionUploading || contentImageStates.some(col => col.some(s => s === 'uploading'));

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: 24,
        minWidth: 420,
        maxWidth: 960,
        width: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        borderRadius: 8,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1f2937' }}>
            {item ? 'Edit' : 'Add'} {isProjects ? 'Project' : isCvProjects ? 'CV Project' : isHomepageImages ? 'Homepage Image' : isHomepageSetup ? 'Homepage Item' : isPageTexts ? 'Page Text' : isBanner ? 'Banner' : 'Event'}
          </h2>
          <button 
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#9ca3af',
              padding: 0,
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        {/* Form Fields Section */}
        <div style={{ marginBottom: 20 }}>
          {fields.map(([key, label, type]) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>{label}</label>
              {type === 'select' ? (
                <select
                  name={key}
                  value={form[key]}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >
                  <option value="solo">Solo</option>
                  <option value="spatial">Spatial</option>
                  <option value="group">Group</option>
                </select>
              ) : type === 'project-select' ? (
                <select
                  name={key}
                  value={form[key] ?? ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: form.linkMode !== 'project' ? '#f3f4f6' : '#fff',
                    cursor: form.linkMode !== 'project' ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit'
                  }}
                  disabled={form.linkMode !== 'project'}
                >
                  <option value="">— No Project (Optional) —</option>
                  {(allProjects || []).map(p => (
                    <option key={p.id} value={p.id}>{p.project_title}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name={key}
                  value={form[key] ?? ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontFamily: 'inherit'
                  }}
                  placeholder={label}
                />
              )}
            </div>
          ))}
        </div>

        {isHomepageSetup && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Project Label</label>
                <input
                  type="text"
                  name="title"
                  value={form.title ?? ''}
                  onChange={handleChange}
                  placeholder="The Silence of the Mole"
                  style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={form.subtitle ?? ''}
                  onChange={handleChange}
                  placeholder="gallery / name / project type"
                  style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Linked Project</label>
              <select
                value={form.project_id || ''}
                onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value ? Number(e.target.value) : null }))}
                style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
              >
                <option value="">-- Select a project --</option>
                {allProjects && allProjects.map(p => (
                  <option key={p.id} value={p.id}>{p.project_title}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Preview Image</label>
              <div style={{ border: '2px dashed #e5e7eb', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', background: '#f9fafb', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                onClick={() => document.getElementById('file-input-homepage-setup')?.click()}
              >
                <input
                  id="file-input-homepage-setup"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const url = await uploadImageToServer(file, 'homepage');
                      setForm(f => ({ ...f, preview_url: url }));
                    } catch (err) {
                      console.error('Homepage preview upload error:', err);
                      alert(err.message || 'Upload failed');
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>📤 Click to upload preview image</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Use the image that appears on hover on the homepage</div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Or paste preview image URL</label>
              <input
                type="text"
                name="preview_url"
                value={form.preview_url ?? ''}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
                style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit' }}
              />
            </div>

            {form.preview_url && (
              <div style={{ marginBottom: 16, background: '#f9fafb', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Preview</div>
                <img src={form.preview_url} alt={form.title || 'homepage preview'} style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 6 }} />
              </div>
            )}
          </div>
        )}

        {/* Homepage Images Section */}
        {isHomepageImages && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_primary ?? false}
                  onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Primary Image</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>(shown first on initial visit)</span>
              </label>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Description</label>
              <textarea
                name="name"
                value={form.name ?? ''}
                onChange={handleChange}
                placeholder="Add a description for this image..."
                style={{
                  width: '100%',
                  minHeight: 80,
                  padding: '10px 12px',
                  fontSize: 14,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Upload Image</label>
              <div style={{
                border: '2px dashed #e5e7eb',
                borderRadius: 8,
                padding: 16,
                textAlign: 'center',
                cursor: 'pointer',
                background: '#f9fafb',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
              onClick={() => document.getElementById('file-input-homepage')?.click()}
              >
                <input
                  id="file-input-homepage"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      // Debug: check if session is available
                      const { data: { session } } = await supabase.auth.getSession();
                      console.log('Homepage upload - session available:', !!session, 'token:', !!session?.access_token);
                      
                      const url = await uploadImageToServer(file, 'homepage');
                      setForm(f => ({ ...f, url }));
                    } catch (err) {
                      console.error('Homepage upload error:', err);
                      alert(err.message || 'Upload failed');
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>📤 Click to upload or drag & drop</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>PNG, JPG, GIF up to 10MB</div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Or paste image URL</label>
              <input
                type="text"
                name="url"
                value={form.url ?? ''}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {form.url && (
              <div style={{ marginBottom: 16, background: '#f9fafb', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>✓ Preview</div>
                <img src={form.url} alt={form.name || 'homepage'} style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6 }} />
              </div>
            )}

            {/* onClick Link Section */}
            <div style={{ marginBottom: 16, background: '#f9fafb', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <label style={{ display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>onClick Link</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'none'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'none', link_project_id: null, link_url: '' }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>No link</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'internal'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'internal', link_url: '' }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>Internal (Project)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'external'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'external', link_project_id: null }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>External URL</span>
                </label>
              </div>

              {form.link_type === 'internal' && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#374151' }}>Select Project</label>
                  <select
                    value={form.link_project_id || ''}
                    onChange={(e) => setForm(f => ({ ...f, link_project_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">-- Select a project --</option>
                    {allProjects && allProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.project_title}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.link_type === 'external' && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#374151' }}>External URL</label>
                  <input
                    type="text"
                    value={form.link_url || ''}
                    onChange={(e) => setForm(f => ({ ...f, link_url: e.target.value }))}
                    placeholder="https://example.com"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {isBanner && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.enabled ?? false}
                  onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Enable Homepage Banner</span>
              </label>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Upload Banner Image</label>
              <div style={{
                border: '2px dashed #e5e7eb',
                borderRadius: 8,
                padding: 16,
                textAlign: 'center',
                cursor: 'pointer',
                background: '#f9fafb',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
              onClick={() => document.getElementById('file-input-banner')?.click()}
              >
                <input
                  id="file-input-banner"
                  type="file"
                  accept="image/svg+xml,image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const url = await uploadImageToServer(file, 'banner');
                      setForm(f => ({ ...f, image_url: url }));
                    } catch (err) {
                      console.error('Banner upload error:', err);
                      alert(err.message || 'Upload failed');
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>📤 Click to upload or drag & drop</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>SVG, PNG, JPG up to 10MB</div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Or paste image URL</label>
              <input
                type="text"
                name="image_url"
                value={form.image_url ?? ''}
                onChange={handleChange}
                placeholder="https://example.com/banner.svg"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Alt text</label>
              <input
                type="text"
                name="alt_text"
                value={form.alt_text ?? ''}
                onChange={handleChange}
                placeholder="Banner alt text"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: 16, background: '#f9fafb', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <label style={{ display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Link Target</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'none'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'none', link_project_id: null, link_url: '' }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>No link</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'internal'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'internal', link_url: '' }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>Internal Project</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                  <input
                    type="radio"
                    checked={form.link_type === 'external'}
                    onChange={() => setForm(f => ({ ...f, link_type: 'external', link_project_id: null }))}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                  <span>External URL</span>
                </label>
              </div>

              {form.link_type === 'internal' && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#374151' }}>Select Project</label>
                  <select
                    value={form.link_project_id || ''}
                    onChange={(e) => setForm(f => ({ ...f, link_project_id: e.target.value ? Number(e.target.value) : null }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">-- Select a project --</option>
                    {allProjects && allProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.project_title}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.link_type === 'external' && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#374151' }}>External URL</label>
                  <input
                    type="text"
                    value={form.link_url || ''}
                    onChange={(e) => setForm(f => ({ ...f, link_url: e.target.value }))}
                    placeholder="https://example.com"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Max Width (%)</label>
                <input
                  type="number"
                  name="max_width"
                  value={form.max_width ?? ''}
                  onChange={handleChange}
                  min="10"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#1f2937' }}>Max Height (px)</label>
                <input
                  type="number"
                  name="max_height"
                  value={form.max_height ?? ''}
                  onChange={handleChange}
                  min="50"
                  max="1200"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 16, background: '#f9fafb', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 10 }}>Live Preview</div>
              <div style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #d1d5db', overflow: 'hidden', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)' }}>
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ width: 36, height: 20, background: '#111827', borderRadius: 6 }} />
                  <div style={{ flex: 1, height: 10, background: '#e5e7eb', borderRadius: 999 }} />
                  <div style={{ width: 90, height: 10, background: '#e5e7eb', borderRadius: 999 }} />
                </div>
                <div style={{ background: '#f3f4f6', padding: 20, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {form.image_url ? (
                    <img
                      src={form.image_url}
                      alt={form.alt_text || 'Banner'}
                      style={{
                        width: form.max_width ? `${form.max_width}%` : '100%',
                        maxHeight: form.max_height ? `${form.max_height}px` : 'auto',
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        background: '#fff'
                      }}
                    />
                  ) : (
                    <div style={{ padding: 18, border: '1px dashed #d1d5db', borderRadius: 8, color: '#9ca3af', background: '#ffffff', width: '100%', textAlign: 'center' }}>Banner preview will appear here.</div>
                  )}
                </div>
                <div style={{ padding: '16px 20px', display: 'grid', gap: 12, background: '#ffffff' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 120, height: 10, background: '#e5e7eb', borderRadius: 999 }} />
                    <div style={{ width: 80, height: 10, background: '#e5e7eb', borderRadius: 999 }} />
                  </div>
                  <div style={{ padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 }}>
                    <div style={{ height: 10, width: '40%', background: '#e5e7eb', borderRadius: 999, marginBottom: 10 }} />
                    <div style={{ height: 10, width: '90%', background: '#e5e7eb', borderRadius: 999, marginBottom: 10 }} />
                    <div style={{ height: 10, width: '80%', background: '#e5e7eb', borderRadius: 999 }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CV Projects Link Section */}
        {isCvProjects && (
          <div style={{ marginBottom: 20, background: '#f9fafb', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <label style={{ display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Link Type</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                <input
                  type="radio"
                  checked={form.linkMode === 'none'}
                  onChange={() => handleLinkModeChange('none')}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <span>No link</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                <input
                  type="radio"
                  checked={form.linkMode === 'project'}
                  onChange={() => handleLinkModeChange('project')}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <span>Link to project</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                <input
                  type="radio"
                  checked={form.linkMode === 'external'}
                  onChange={() => handleLinkModeChange('external')}
                  style={{ cursor: 'pointer', width: 16, height: 16 }}
                />
                <span>External URL</span>
              </label>
            </div>
            {form.linkMode === 'external' && (
              <input
                type="text"
                name="external_url"
                value={form.external_url ?? ''}
                onChange={handleChange}
                placeholder="https://example.com"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontFamily: 'inherit'
                }}
              />
            )}
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
              For internal projects, use the "Link to Project" dropdown above to select a project.
            </div>
          </div>
        )}

        {/* Page Texts Text Section */}
        {isPageTexts && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>Text Content</label>
            <textarea
              name="text"
              value={form.text ?? ''}
              onChange={handleChange}
              rows={8}
              placeholder="Enter the text content..."
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
        )}

        {/* Projects Description Section */}
        {(isProjects) && (
          <div style={{ marginBottom: 20 }}>
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 16,
              background: '#f9fafb'
            }}>
              <label style={{ fontWeight: 600, marginBottom: 12, display: 'block', fontSize: 14, color: '#1f2937' }}>
                Project Page Text
              </label>
              <RichTextEditor
                value={form.description}
                onChange={(nextValue) => setForm(f => ({ ...f, description: nextValue ?? '' }))}
                onUploadStateChange={setDescriptionUploading}
                onUploadImage={(file) => uploadImageToServer(file, item?.id || 'draft-project')}
                minHeight={220}
              />
            </div>
          </div>
        )}

        {/* Projects Content Columns */}
        {isProjects && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600, color: '#1f2937' }}>Content Blocks</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
              {form.content.map((col, colIdx) => (
                <div key={`col-${colIdx}`} style={{ border: '1px solid #e5e7eb', padding: 16, borderRadius: 8, minHeight: 160, background: '#ffffff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{colIdx === 0 ? '← Left Column' : 'Right Column →'}</h4>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button 
                        type="button" 
                        onClick={() => handleAddBlock(colIdx, 'image')}
                        style={{
                          padding: '4px 8px',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#c7d2fe'}
                        onMouseLeave={(e) => e.target.style.background = '#e0e7ff'}
                      >
                        + Image
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleAddBlock(colIdx, 'video')}
                        style={{
                          padding: '4px 8px',
                          background: '#dbeafe',
                          color: '#1e40af',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#bfdbfe'}
                        onMouseLeave={(e) => e.target.style.background = '#dbeafe'}
                      >
                        + Video
                      </button>
                    </div>
                  </div>
                  <div>
                    {col.map((block, idx) => {
                      const state = (contentImageStates[colIdx] && contentImageStates[colIdx][idx]) || 'idle';
                      return (
                        <div
                          key={`block-${colIdx}-${idx}`}
                          draggable
                          onDragStart={() => handleContentDragStart(colIdx, idx)}
                          onDragOver={e => e.preventDefault()}
                          onDrop={() => handleContentDrop(colIdx, idx)}
                          style={{
                            padding: 12,
                            marginBottom: 8,
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: '#f9fafb',
                            position: 'relative',
                            cursor: 'grab'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', textTransform: 'capitalize', background: block.type === 'image' ? '#fef3c7' : block.type === 'video' ? '#dbeafe' : '#f3f4f6', padding: '2px 8px', borderRadius: 3 }}>
                              {block.type === 'image' ? '🖼️' : block.type === 'video' ? '▶️' : '📝'} {block.type}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveBlock(colIdx, idx)}
                              style={{
                                background: '#fee2e2',
                                color: '#991b1b',
                                border: 'none',
                                borderRadius: 4,
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 500,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#fecaca'}
                              onMouseLeave={(e) => e.target.style.background = '#fee2e2'}
                            >
                              ✕ Remove
                            </button>
                          </div>
                          {block.type === 'text' && (
                            <textarea
                              value={block.value ?? ''}
                              onChange={(e) => handleEditBlock(colIdx, idx, e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: 100,
                                padding: '8px 10px',
                                fontSize: 13,
                                border: '1px solid #e5e7eb',
                                borderRadius: 4,
                                fontFamily: 'monospace',
                                resize: 'vertical'
                              }}
                              placeholder="Enter text content..."
                            />
                          )}
                          {block.type === 'video' && (
                            <div>
                              <input
                                type="text"
                                placeholder="https://example.com/video"
                                value={block.value ?? ''}
                                onChange={(e) => handleEditBlock(colIdx, idx, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 10px',
                                  fontSize: 13,
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 4,
                                  fontFamily: 'inherit'
                                }}
                              />
                              {block.value && <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', wordBreak: 'break-all' }}>🔗 {block.value}</div>}
                            </div>
                          )}
                          {block.type === 'image' && (
                            <div>
                              {block.value ? (
                                <div>
                                  <img src={block.value} alt={`block-${colIdx}-${idx}`} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, marginBottom: 10 }} />
                                  <div style={{ marginBottom: 10 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#1f2937' }}>Description</label>
                                    <textarea
                                      placeholder="Add a caption for this image..."
                                      value={block.description || ''}
                                      onChange={(e) => handleEditBlock(colIdx, idx, block.value, e.target.value)}
                                      style={{
                                        width: '100%',
                                        minHeight: 60,
                                        padding: '8px 10px',
                                        fontSize: 12,
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 4,
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                      }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <input id={`file-input-${colIdx}-${idx}`} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleBlockImageUpload(e, colIdx, idx)} />
                                  {state === 'uploading' && (
                                    <div style={{ textAlign: 'center', color: '#2563eb', fontSize: 13 }}>⟳ Uploading…</div>
                                  )}
                                  {state === 'error' && (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span style={{ fontSize: 12, color: '#991b1b' }}>✕ Upload failed</span>
                                      <button
                                        type="button"
                                        onClick={() => retryBlockUpload(colIdx, idx)}
                                        style={{
                                          border: '1px solid #fecaca',
                                          padding: '4px 8px',
                                          borderRadius: 4,
                                          cursor: 'pointer',
                                          background: '#fff',
                                          fontSize: 12,
                                          color: '#991b1b',
                                          fontWeight: 500
                                        }}
                                      >
                                        Retry
                                      </button>
                                    </div>
                                  )}
                                  {state === 'idle' && (
                                    <label
                                      style={{
                                        display: 'block',
                                        border: '2px dashed #e5e7eb',
                                        padding: '16px',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        background: '#f9fafb',
                                        textAlign: 'center',
                                        transition: 'all 0.2s',
                                        fontSize: 13,
                                        color: '#6b7280',
                                        fontWeight: 500
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                                      onClick={() => { const input = document.getElementById(`file-input-${colIdx}-${idx}`); if (input) input.click(); }}
                                    >
                                      📤 Click to upload
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* allow dropping cards at end */}
                    <div onDragOver={(e) => e.preventDefault()} onDrop={() => handleContentDrop(colIdx, form.content[colIdx].length)} style={{ minHeight: 8, marginBottom: 8 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 20 }}>
          <button
            onClick={handleSubmit}
            disabled={saving || anyUploading}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: saving || anyUploading ? '#d1d5db' : '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: saving || anyUploading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: 14,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => { if (!(saving || anyUploading)) e.target.style.background = '#1d4ed8'; }}
            onMouseLeave={(e) => { if (!(saving || anyUploading)) e.target.style.background = '#2563eb'; }}
          >
            {saving ? '⟳ Saving…' : anyUploading ? '⟳ Wait for uploads…' : '✓ Save'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '10px 16px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: 14,
              color: '#6b7280',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { if (!saving) { e.target.style.background = '#e5e7eb'; e.target.style.color = '#1f2937'; } }}
            onMouseLeave={(e) => { if (!saving) { e.target.style.background = '#f3f4f6'; e.target.style.color = '#6b7280'; } }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminModal;
